import { FC, useRef, useEffect, useState } from 'react';
import CarouselBottomBar from './elements/carousel-bottom-bar';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { handleVideoFocus } from './handle-video-focus';
import { TextButtonProps } from '../TextButton';
import {
  ProductSection,
  ProductGridContainer,
} from './product-carousel.styled';
import { ProductCarouselItemType } from 'components/home/content/product-carousel.content';
import CarouselItem from './carousel-item';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import ProductHeadlineComponent from './elements/product-headline';
import CarouselButton from './elements/carousel-button';

export type aligns = 'left' | 'center';

interface ProductGridProps {
  id?: string;
  title: string;
  body: string;
  items: ProductCarouselItemType[];
  primaryButton?: TextButtonProps;
  align?: aligns;
  color?: 'seedgreen' | 'snowwhite';
}

const ProductGrid: FC<ProductGridProps> = ({
  id,
  title,
  body,
  items,
  primaryButton,
  align = 'left',
  color = 'seedgreen',
}) => {
  const videoRefs = useRef<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [prevIndex, setPrevIndex] = useState<number>(-1);
  const isSmall = useClientSideMediaQuery(960);

  const handleHover = (index: number) => {
    if (index !== activeIndex && !isSmall) {
      setPrevIndex(activeIndex ?? 0);
      setActiveIndex(index);
    }
  };

  const handleClick = (index: number, label: string) => {
    handleHover(index);
    buttonEventHandler(label, 'Product Carousel');
  };

  useEffect(() => {
    handleVideoFocus(videoRefs, activeIndex, prevIndex);
  }, [activeIndex, prevIndex]);

  // Videos play only on hover, so small screens reset them to their posters.
  useEffect(() => {
    if (isSmall) {
      videoRefs.current.forEach((video) => video?.load());
      setActiveIndex(undefined);
    }
  }, [isSmall]);

  const textColor = color === 'seedgreen' ? 'inverse-primary' : 'primary';

  const primaryButtonNode = primaryButton ? (
    <CarouselButton button={primaryButton} color={color} />
  ) : undefined;

  return (
    <ProductSection id={id} $color={color}>
      <ProductHeadlineComponent
        title={title}
        body={body}
        textColor={textColor}
        color={color}
        align={align}
        primaryButton={primaryButtonNode}
      />

      <ProductGridContainer>
        {items.map((item: ProductCarouselItemType, index: number) => (
          <CarouselItem
            key={index}
            item={item}
            index={index}
            color={color}
            activeIndex={activeIndex}
            isSmall={isSmall}
            onHover={handleHover}
            onClick={handleClick}
            videoRefs={videoRefs}
            loadVideo={!isSmall}
          />
        ))}
      </ProductGridContainer>

      {isSmall && (primaryButton || items) && (
        <CarouselBottomBar
          color={color}
          button={primaryButton}
          items={items}
          showDot={false}
          justify='end'
        />
      )}
    </ProductSection>
  );
};

export default ProductGrid;
